/**
 * useInstallPrompt — Chrome/Android PWA install prompt orchestration.
 *
 * Captures the deferred `beforeinstallprompt` event so we can fire
 * `.prompt()` later at our own UX-appropriate moment (after the user
 * has shown engagement). iOS has no such API; the caller handles the
 * "Add to Home Screen" coaching copy separately via the isIOS flag.
 *
 * The prompt becomes visible only when:
 *   - the device is mobile/tablet (no point on desktop)
 *   - the app isn't already installed (display-mode standalone)
 *   - the user hasn't dismissed it before (fornland-install-dismissed)
 *   - the welcome modal isn't open (don't stack overlays)
 *   - the user has opened at least one detail panel (engagement signal)
 *   - this is a returning visit, not the first (since 26 Sep 2026)
 *   - no detail panel is open right now (since 26 Sep 2026)
 *   - 8 s have passed since that engagement (avoid surprise on tap)
 *
 * The last three were one rule until four of six simulated reviewers met
 * the sheet on their first visit, over the record they had just opened.
 *
 * `canPromptNatively` mirrors the deferredPrompt presence as state so
 * render-side gates don't have to dereference a ref.
 *
 * Consumers: App.tsx render (install banner + buttons).
 * Companion: useDevice (provides the isMobileOrTablet/isStandalone gates).
 */
import { useCallback, useEffect, useRef, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface UseInstallPromptParams {
  isMobileOrTablet: boolean;
  isStandalone: boolean;
  showWelcome: boolean;
  hasEngaged: boolean;
  /** A camera was saved by an earlier visit: this is not the first one. */
  returning: boolean;
  /** A detail panel is open; the sheet would cover what the reader chose. */
  panelOpen: boolean;
}

export interface UseInstallPromptReturn {
  showInstallPrompt: boolean;
  canPromptNatively: boolean;
  dismissInstall: () => void;
  handleInstall: () => Promise<void>;
}

export function useInstallPrompt({ isMobileOrTablet, isStandalone, showWelcome, hasEngaged, returning, panelOpen }: UseInstallPromptParams): UseInstallPromptReturn {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [canPromptNatively, setCanPromptNatively] = useState(false);
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
      setCanPromptNatively(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    if (isStandalone) return;
    if (!isMobileOrTablet) return;
    if (localStorage.getItem("fornland-install-dismissed")) return;
    if (showWelcome) return;
    if (!hasEngaged || !returning) return;
    if (panelOpen) { setShowInstallPrompt(false); return; }
    const timer = setTimeout(() => setShowInstallPrompt(true), 8_000);
    return () => clearTimeout(timer);
  }, [showWelcome, isStandalone, isMobileOrTablet, hasEngaged, returning, panelOpen]);

  const dismissInstall = useCallback(() => {
    setShowInstallPrompt(false);
    localStorage.setItem("fornland-install-dismissed", "1");
  }, []);

  const handleInstall = useCallback(async () => {
    if (deferredPromptRef.current) {
      deferredPromptRef.current.prompt();
      await deferredPromptRef.current.userChoice;
      deferredPromptRef.current = null;
      setCanPromptNatively(false);
    }
    dismissInstall();
  }, [dismissInstall]);

  return { showInstallPrompt, canPromptNatively, dismissInstall, handleInstall };
}
